import { ChatLinkContextMenu } from "./chat/ChatLinkContextMenu"
import React, { useCallback, useContext, useMemo, useState } from "react"

import { toast } from "sonner"
import AvatarImage from "./AvatarImage"
import { changeActiveChat, Chat } from "../reducers/chatsSlice"
import { useAppDispatch, useAppSelector } from "../reducers/hooks"
import useChatInfo from "../hooks/useChatInfo"
import { selectChatSubject } from "../reducers/subjectsSlice"
import { isMobile } from "../utils/detectDevice"
import { isObuRoomGroup } from "../lib/slickAISilence"
import { Button } from "./ui/button"
import { ArrowLeft, Phone, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { XmppContext } from "../stream/xmppClient"
import { removeBookmark } from "../api/xeps/PepNativeBookmarks"
import { selectRoomChatCount } from "../reducers/chatsSliceSelectors"
import {
  changeActiveChatToFirstNotArchivedDriver,
  leaveChat,
} from "../reducers/chatsSliceThunks"
import ChatHelpers from "../lib/chatHelpers"
import SearchPopover from "./search/SearchPopover"
import {
  hideArchive,
  hideChatSearch,
  isArchiveVisible,
  isChatSearchVisible,
  showChatSearch,
} from "../reducers/overlaysSlice"
import { mobileRoutes, setMobileRoute } from "../routes/routes"
import { AddEditUserModal } from "./userManagement/AddEditUserModal"
import { AddEditDriverModal } from "./driverManagement/AddEditDriverModal"
import { EditCrewModal } from "./driverManagement/EditCrewModal"
import { selectAccountChatByJid } from "../reducers/accountChatsSlice"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { useUserPermissions } from "@/hooks/useUserPermissions"
import {
  AgentContextLine,
  ClassicContextLine,
} from "@/components/chat/ChatHeaderContextLine"
import { CmrSearchDialog } from "./cmrReport/CmrSearchDialog"
import { changeActiveTab, TAB_TYPES } from "../reducers/tabsSlice"
import { findSlickAIProfile } from "../reducers/profilesSliceSelectors"
import { extractVhostFromMuc, extractVhostFromUser } from "../jidUtils"
import { resolveDriverJidForChat } from "../lib/resolveDriverForChat"
import { TimeProgressBar } from "./tacho/TimeProgressBar"
import {
  selectArchivedChats,
  selectChatSettings,
} from "@/reducers/chatsSettingsSelectors"
import { useChatTachos } from "@/hooks/useChatTachos"
import { publishChatSettings } from "@/api/xeps/ChatsSettings"
import { useMediaQuery } from "@react-hookz/web"
import DriverButton from "@/components/driverButton/driverButton"
import { createSelectDriversWithSameDevice } from "@/reducers/gpsConfigSelectors"
import { createSelectComplementaryDrivers } from "@/reducers/accountChatsSelectors"
import { selectLocation } from "@/reducers/locationsSelectors"
import CMRHeader from "./CMRHeader"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  selectLocationJidFromChatJid,
  selectRouteJidFromChatJid,
} from "@/reducers/selectors"
import { selectUserChats } from "@/reducers/userChatsSlice"
import { findTranschatUserChat } from "@/lib/privateAgentUtils"
import ClipboardWriter from "@/lib/clipboardWriter"
import { useDriverRoomIssues } from "@/hooks/useDriverRoomIssues"
import { buildIssueLines } from "@/components/DriverRoomIssueIndicator"
import { useTranslation } from "react-i18next"
import ChatHeaderRoomActions from "@/components/chat/ChatHeaderRoomActions"
import { composeChatHeaderTitle } from "@/lib/chatHeaderTitle"

const shortenFirstName = (fullName: string | undefined): string => {
  if (!fullName) return ""
  const parts = fullName.trim().split(/\s+/)
  if (parts.length < 2) return fullName
  return `${parts[0][0]}. ${parts.slice(1).join(" ")}`
}

const canEditRoom = (
  userJid: string | undefined | null,
  roomJid: string | undefined | null,
) =>
  userJid &&
  roomJid &&
  extractVhostFromMuc(roomJid) === extractVhostFromUser(userJid)

const hasNumericValue = (value: number | undefined): value is number =>
  typeof value === "number" && !Number.isNaN(value)

const formatFuelLevel = (fuelLevel: number): string => {
  const normalizedFuelLevel =
    fuelLevel >= 0 && fuelLevel <= 1 ? fuelLevel * 100 : fuelLevel
  return `${Math.round(normalizedFuelLevel)}%`
}

const formatFuelLiters = (fuelLiters: number): string =>
  `${Math.round(fuelLiters)} l`

type ChatHeaderProp = {
  chat: Chat
  onReturnToChatLists: () => void
  onOpenChatInfo: () => void
  onActiveChatChange: (chatJid: string) => void
  titleOverride?: string
  hideActions?: boolean
  minimal?: boolean
}

const ChatHeader = ({
  chat,
  onReturnToChatLists,
  onOpenChatInfo,
  onActiveChatChange,
  titleOverride,
  hideActions,
  minimal,
}: ChatHeaderProp) => {
  const { t } = useTranslation()
  const { client, myJid } = useContext(XmppContext)
  const dispatch = useAppDispatch()
  const chatsCount = useAppSelector(selectRoomChatCount)
  const slickAIProfile = useAppSelector(findSlickAIProfile)
  const isSmallScreen = useMediaQuery("(max-width: 900px)")

  // The room address names whoever drove this car first, so the crew config is
  // the only thing that says who drives it today.
  const accountChat = useAppSelector(selectAccountChatByJid(chat.jid))
  const driverJid = resolveDriverJidForChat(accountChat, chat.jid)
  const locationJid = useAppSelector(selectLocationJidFromChatJid(chat.jid))

  const currentLocation = useAppSelector(selectLocation(locationJid))

  const {
    driverParticipant,
    chatTitle,
    photoSrc,
    additionalInfo,
    vehicleLabel,
    driverPhone,
    driverBPhone,
    driverAName,
    driverBName,
  } = useChatInfo(chat)

  const headerTitle = useMemo(
    () => (minimal ? chatTitle : composeChatHeaderTitle(chat.name, chatTitle)),
    [minimal, chat.name, chatTitle],
  )

  const chatSettings = useAppSelector(selectChatSettings(chat.jid))
  const isMuted = Boolean(chatSettings?.muted)
  const isArchived = Boolean(chatSettings?.archived)

  const subject = useAppSelector(selectChatSubject(chat.jid))
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isEditDriverModalOpen, setIsEditDriverModalOpen] = useState(false)
  const [cmrSearchOpen, setCmrSearchOpen] = useState(false)
  const [isMobileSubjectExpanded, setIsMobileSubjectExpanded] = useState(false)
  const { tachos, isDoubleCrew: isDoubleCrewTacho } = useChatTachos(chat.jid)
  const issues = useDriverRoomIssues(chat.jid)

  const isCMRRoom = useMemo(() => ChatHelpers.isCMRRoom(chat), [chat])
  const isSearchV2Enabled = Boolean(useFeatureFlagEnabled("cmr-search-v2"))
  const useNewCmrSearch = isCMRRoom && isSearchV2Enabled
  const isFuelReportEnabled = Boolean(useFeatureFlagEnabled("fuel-report"))

  const onExitChat = async (e: React.MouseEvent) => {
    e.stopPropagation()
    onReturnToChatLists()
    await removeBookmark(client, chat.jid)
    dispatch(leaveChat(client, chat))
  }

  const handleShowSearch = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      // @ts-ignore
      e.currentTarget?.blur?.()
      if (useNewCmrSearch) {
        setCmrSearchOpen(true)
        return
      }
      dispatch(showChatSearch())
      if (isMobile || isSmallScreen) {
        setMobileRoute(mobileRoutes.SEARCH)
      }
    },
    [dispatch, useNewCmrSearch, isSmallScreen],
  )

  const archiveVisible = useAppSelector(isArchiveVisible)

  const onBackClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (archiveVisible) {
      setMobileRoute(mobileRoutes.ARCHIVE)
    }
    onReturnToChatLists()
  }

  const handleCloseSearch = useCallback(() => {
    if (isMobile || isSmallScreen) {
      setMobileRoute(mobileRoutes.CHAT)
    }
    dispatch(hideChatSearch())
  }, [dispatch, isSmallScreen])

  const handleEditExternalContact = useCallback(
    (e: React.MouseEvent) => {
      e?.stopPropagation()
      setIsEditModalOpen(true)
    },
    [setIsEditModalOpen],
  )

  const handleCloseEditExternalContact = useCallback(
    (e: React.MouseEvent) => {
      e?.stopPropagation()
      setIsEditModalOpen(false)
    },
    [setIsEditModalOpen],
  )

  const handleEditDriver = useCallback(
    (e: React.MouseEvent) => {
      e?.stopPropagation()
      setIsEditDriverModalOpen(true)
    },
    [setIsEditDriverModalOpen],
  )

  const handleCloseEditDriver = useCallback(
    (e: React.MouseEvent) => {
      e?.stopPropagation()
      setIsEditDriverModalOpen(false)
    },
    [setIsEditDriverModalOpen],
  )

  const searchVisible = useAppSelector(isChatSearchVisible)

  const isExternalContact = ChatHelpers.isChatWithExternalContact(chat)
  const [firstName, lastName] = useMemo(() => {
    const [first, ...rest] = chat.name.split(" ")
    return [first, rest.join(" ")]
  }, [chat.name])
  const externalContactPhone = isExternalContact
    ? ChatHelpers.getExternalContactPhone(chat)
    : undefined

  let handleEdit = null
  if (isExternalContact && !isMobile && !isSmallScreen) {
    handleEdit = handleEditExternalContact
  }
  if (canEditRoom(client?.jid?.toString(), chat.jid)) {
    handleEdit = handleEditDriver
  }

  const privateAgentEnabled = Boolean(useFeatureFlagEnabled("private-agent"))
  const userChats = useAppSelector(selectUserChats)
  const tachoEnabled = true
  const ownTranschatJid = useMemo(
    () => findTranschatUserChat(userChats, myJid.local)?.jid,
    [userChats, myJid.local],
  )
  const isChatWithAssistant = useMemo(
    () => ChatHelpers.isChatWithAssistant(chat),
    [chat],
  )
  const isOwnTranschatChat = useMemo(
    () => Boolean(ownTranschatJid && chat.jid === ownTranschatJid),
    [chat.jid, ownTranschatJid],
  )

  const isCarIssuesRoom = useMemo(
    () => ChatHelpers.isCarIssuesRoom(chat),
    [chat],
  )

  const archivedChats = useAppSelector(selectArchivedChats)

  const toggleChatMuted = useCallback(async () => {
    await publishChatSettings(client, {
      ...(chatSettings || {}),
      chatJid: chat.jid,
      muted: !isMuted,
    })
  }, [client, chat.jid, chatSettings, isMuted])

  const toggleChatArchived = useCallback(async () => {
    await publishChatSettings(client, {
      ...(chatSettings || {}),
      chatJid: chat.jid,
      archived: !isArchived,
    })
    if (!isArchived) {
      dispatch(changeActiveChatToFirstNotArchivedDriver(chat.jid))
    } else {
      const archivedWithoutCurrent = archivedChats.filter(
        (chatJid) => chatJid !== chat.jid,
      )
      if (archivedWithoutCurrent.length === 0) {
        dispatch(hideArchive())
        dispatch(changeActiveChat({ activeChatJid: chat.jid }))
      } else {
        dispatch(changeActiveChat({ activeChatJid: archivedWithoutCurrent[0] }))
      }
    }
  }, [client, chatSettings, chat.jid, isArchived, dispatch, archivedChats])

  // On a managed account the fleet manager decides which chats a dispatcher
  // keeps, so leaving is not theirs to do.
  const { membershipLocked } = useUserPermissions()
  const canLeaveChat =
    chatsCount > 1 && ChatHelpers.canUserLeaveChat(chat) && !membershipLocked
  const canOpenChatInfo =
    chat.room && !isMobile && !isSmallScreen && !isExternalContact

  const selectDriversWithSameDevice = useMemo(
    () => createSelectDriversWithSameDevice(driverJid),
    [driverJid],
  )

  const driversWithSameGps = useAppSelector(selectDriversWithSameDevice)

  const myVhost = extractVhostFromUser(myJid.toString())

  const chatVhost = extractVhostFromMuc(chat.jid)
  const routeJid = useAppSelector(selectRouteJidFromChatJid(chat.jid))
  const isConference = accountChat?.group === "conference"
  // OBU-bridged chats have no driver/crew to edit; EditCrewModal requires one
  const isObuChat = isObuRoomGroup(accountChat?.group)
  const canEdit =
    !isConference &&
    !isObuChat &&
    (isExternalContact || canEditRoom(client?.jid?.toString(), chat.jid))
  // A car chat belongs in the crew modal even before it has a car or a driver
  // - that modal is the only place left to give it either.
  const usesCrewModal = Boolean(
    accountChat?.driverA ||
      accountChat?.carId ||
      accountChat?.group === "driver" ||
      accountChat?.group === "double_crew",
  )
  const isDoubleCrew = Boolean(accountChat?.driverB)
  const isSharedDriverChat = chatVhost !== myVhost
  const selectComplementaryDrivers = useMemo(
    () => createSelectComplementaryDrivers(chat.jid, isSharedDriverChat),
    [chat.jid, isSharedDriverChat],
  )
  const complementaryDrivers = useAppSelector(selectComplementaryDrivers)

  const driversToSwitch = useMemo(() => {
    const seen = new Set<string>()
    const candidates: {
      driverJid?: string
      chatJid?: string
      chatName?: string
    }[] = [
      ...Object.values(driversWithSameGps).map(({ driverJid }) => ({
        driverJid,
      })),
      ...complementaryDrivers,
    ]

    return candidates
      .map((config) => {
        // A complementary room between drivers has no driver to ask, so the
        // room itself says which side of the share it is on.
        const vhost = config.driverJid
          ? extractVhostFromUser(config.driverJid)
          : config.chatJid
            ? extractVhostFromMuc(config.chatJid)
            : myVhost
        const isSharedDriver = vhost !== myVhost
        const isCoDriver =
          !isSharedDriver &&
          Boolean(config.driverJid) &&
          extractVhostFromMuc(chat.jid) === vhost
        return {
          driverJid: config.driverJid,
          chatJid: config.chatJid,
          chatName: config.chatName,
          isSharedDriver,
          isCoDriver,
        }
      })
      .filter((driver) => {
        // Keyed on the room when we have one: two rooms can hold the same
        // driver, and collapsing them hides the one the office wants.
        const key = driver.chatJid ?? driver.driverJid
        if (!key || seen.has(key)) {
          return false
        }
        seen.add(key)
        return true
      })
  }, [driversWithSameGps, complementaryDrivers, myVhost, chat.jid])

  const odometerValue = currentLocation?.odometer
  const fuelLevelValue = currentLocation?.fuelLevel
  const fuelLitersValue = currentLocation?.fuelLiters
  const odometerLabel = hasNumericValue(odometerValue)
    ? `${Math.round(odometerValue)} km`
    : undefined
  const fuelLevelLabel = hasNumericValue(fuelLevelValue)
    ? formatFuelLevel(fuelLevelValue)
    : undefined
  const fuelLitersLabel = hasNumericValue(fuelLitersValue)
    ? formatFuelLiters(fuelLitersValue)
    : undefined
  const titleClass = cn(
    "truncate text-sm font-semibold",
    minimal ? "text-foreground" : "text-sidebar-foreground",
  )

  const additionalInfoToCopy = useMemo(() => {
    if (accountChat?.carId) {
      return additionalInfo
    }

    if (accountChat?.driverA) {
      return additionalInfo.trim().split(/\s+/)[0] || ""
    }

    return ""
  }, [accountChat?.carId, accountChat?.driverA, additionalInfo])

  const contextLine = {
    vehicleLabel: minimal ? vehicleLabel : additionalInfo,
    vehicleCopyText: additionalInfoToCopy,
    vehicleCopyLabel: accountChat?.carId
      ? t("chatHeader.copyCarName")
      : t("chatHeader.copyPlates"),
    odometerLabel,
    fuelLevelLabel,
    fuelLitersLabel,
    fuelReportEnabled: isFuelReportEnabled,
    driverJid,
    chatJid: chat.jid,
  }

  const hasSubject = useMemo(() => {
    return subject && !!subject.text
  }, [subject])

  const handleCopyJid = useCallback(
    (e: React.MouseEvent) => {
      if (
        e.metaKey &&
        (window as unknown as { __SLICKSHIFT_EXTENSION_LOADED__?: boolean })
          .__SLICKSHIFT_EXTENSION_LOADED__
      ) {
        e.stopPropagation()
        ClipboardWriter.executeAndWriteResult(async () => chat.jid)
        toast(t("chatHeader.jidCopied"))
      }
    },
    [chat.jid, t],
  )

  const withoutChatList =
    (isChatWithAssistant && privateAgentEnabled && isOwnTranschatChat) ||
    isCMRRoom ||
    isCarIssuesRoom

  return (
    <>
      {isEditModalOpen && (
        <AddEditUserModal
          isOpen={isEditModalOpen}
          firstName={firstName}
          lastName={lastName}
          phoneNumber={externalContactPhone}
          onClose={handleCloseEditExternalContact}
        />
      )}
      {isEditDriverModalOpen && usesCrewModal && (
        <EditCrewModal
          isOpen={isEditDriverModalOpen}
          mucJid={chat.jid}
          currentDriverAJid={accountChat?.driverA}
          currentDriverBJid={accountChat?.driverB}
          currentName={chat.name}
          currentCarId={accountChat?.carId}
          currentTmsCarId={accountChat?.tmsCarId}
          currentTmsTrailerId={accountChat?.tmsTrailerId}
          onClose={handleCloseEditDriver}
          group={accountChat?.group}
        />
      )}
      {isEditDriverModalOpen && !usesCrewModal && (
        <AddEditDriverModal
          isOpen={isEditDriverModalOpen}
          userJID={driverParticipant?.jid}
          routeJid={routeJid}
          firstName={driverParticipant?.firstName}
          lastName={driverParticipant?.lastName}
          phoneNumber={driverParticipant?.phone}
          plates={driverParticipant?.plates}
          onClose={handleCloseEditDriver}
          isDoubleCrew={isDoubleCrew}
        />
      )}
      <div
        className={cn(
          "flex flex-col",
          minimal
            ? // No divider — a downward gradient fade instead (see below), so
              // the thread dissolves into the bar as it scrolls under it. Bar
              // shares the thread's bg.
              "relative z-10"
            : "border-b border-border",
        )}
      >
        <div
          className={cn(
            "flex items-center gap-2 px-4",
            minimal
              ? "min-h-20 flex-wrap bg-sidebar py-2"
              : "min-h-16 bg-background py-2",
          )}
        >
          {/* The split driver-chat column shows the chat list and the
          thread side by side, so there is nowhere for "back" to go —
          only the classic single-thread header returns to a chat list. */}
          {!minimal && (
            <Button
              data-testid="exit-chat-button"
              variant="ghost"
              size="icon"
              onClick={onBackClick}
              className={cn("md:hidden")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          {!minimal &&
            !withoutChatList &&
            !isChatWithAssistant &&
            !titleOverride && (
              <AvatarImage
                color={ChatHelpers.getChatColor(chat)}
                name={chatTitle}
                src={photoSrc}
                size="small"
              />
            )}
          <div
            className={cn(
              "flex min-w-0 gap-2",
              minimal
                ? "flex-[1_1_20rem] max-md:w-full max-md:flex-none"
                : "flex-1",
            )}
          >
            <div className="flex min-w-0 flex-col justify-center">
              <div className="flex min-w-0 items-baseline gap-2">
                <ChatLinkContextMenu
                  key={chat.jid}
                  jid={chat.room ? chat.jid : undefined}
                >
                  {withoutChatList ? (
                    <span
                      data-testid="chat-title"
                      className="truncate text-sm font-semibold text-sidebar-foreground"
                      onClick={handleCopyJid}
                    >
                      <h1 className="text-2xl font-bold text-foreground">
                        {isCMRRoom ? (
                          <CMRHeader />
                        ) : isCarIssuesRoom ? (
                          t("chatHeader.carAccidents")
                        ) : (
                          slickAIProfile?.name
                        )}
                      </h1>
                    </span>
                  ) : driverBName ? (
                    <div className="flex min-w-0 flex-col gap-0.5 md:flex-row md:items-baseline md:gap-2">
                      <div className="flex min-w-0 items-baseline gap-2">
                        <span
                          data-testid="chat-title"
                          className={titleClass}
                          onClick={handleCopyJid}
                          title={driverAName}
                        >
                          {shortenFirstName(driverAName)}
                        </span>
                        {!minimal && driverPhone && (
                          <span className="truncate text-sm text-blue-700 max-md:hidden">
                            <a href={`tel:${driverPhone}`} target="_parent">
                              {driverPhone}
                            </a>
                          </span>
                        )}
                      </div>
                      <div className="flex min-w-0 items-baseline gap-2">
                        <span className={titleClass} title={driverBName}>
                          {shortenFirstName(driverBName)}
                        </span>
                        {!minimal && driverBPhone && (
                          <span className="truncate text-sm text-blue-700 max-md:hidden">
                            <a href={`tel:${driverBPhone}`} target="_parent">
                              {driverBPhone}
                            </a>
                          </span>
                        )}
                      </div>
                    </div>
                  ) : titleOverride ? (
                    <h1 className="text-2xl font-bold text-foreground">
                      {titleOverride}
                    </h1>
                  ) : (
                    <span
                      data-testid="chat-title"
                      className={titleClass}
                      onClick={handleCopyJid}
                      title={isChatWithAssistant ? undefined : headerTitle}
                    >
                      {isChatWithAssistant ? "Slick AI" : headerTitle}
                    </span>
                  )}
                </ChatLinkContextMenu>
                {!minimal && !driverBName && driverPhone && (
                  <span className="truncate text-sm text-blue-700 max-md:hidden">
                    <a href={`tel:${driverPhone}`} target="_parent">
                      {driverPhone}
                    </a>
                  </span>
                )}
                {!(isMobile || isSmallScreen) &&
                  driversToSwitch.length > 0 &&
                  driversToSwitch.map((driver) => (
                    <DriverButton
                      key={driver.chatJid ?? driver.driverJid}
                      {...driver}
                      onActiveChatChange={onActiveChatChange}
                    />
                  ))}
                <span className="truncate text-sm text-muted-foreground max-md:hidden">
                  {isExternalContact ? externalContactPhone : ""}
                </span>
              </div>
              {!minimal && hasSubject && (
                <button
                  type="button"
                  className={cn(
                    "min-w-0 cursor-pointer text-left text-xs text-muted-foreground md:hidden",
                    isMobileSubjectExpanded
                      ? "whitespace-pre-wrap break-words"
                      : "truncate",
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsMobileSubjectExpanded((v) => !v)
                  }}
                  aria-expanded={isMobileSubjectExpanded}
                  title={subject?.text}
                >
                  {subject?.text}
                </button>
              )}
              {minimal ? (
                <AgentContextLine {...contextLine} />
              ) : (
                <ClassicContextLine
                  {...contextLine}
                  subjectText={subject?.text}
                />
              )}
            </div>
            {(isMobile || isSmallScreen) && isExternalContact && (
              <div className="ml-auto">
                <a
                  className="!text-foreground"
                  href={`tel:${externalContactPhone}`}
                  target="_parent"
                >
                  <Button variant="ghost" size="icon">
                    <Phone className="h-4 w-4 text-foreground" />
                  </Button>
                </a>
              </div>
            )}
          </div>
          <div
            className={cn(
              "flex items-center gap-2",
              minimal && "max-md:w-full max-md:justify-end max-md:gap-1",
              // Bigger icons — matching the left rail's weight (the default
              // 16px reads too small in this bar).
              minimal && "[&_svg]:!size-5",
            )}
          >
            {driverPhone && driverBPhone ? (
              <DropdownMenu>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <Button
                          data-testid="call-driver-button"
                          aria-label={t("chatHeader.callDriver")}
                          variant="ghost"
                          size="icon"
                        >
                          <Phone className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>{t("chatHeader.callDriver")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <a
                      className="!text-foreground no-underline hover:no-underline"
                      href={`tel:${driverPhone}`}
                      target="_parent"
                    >
                      <Phone className="h-4 w-4 shrink-0" />
                      <span className="font-medium">{driverAName}</span>
                      <span className="ml-auto pl-4 text-muted-foreground">
                        {driverPhone}
                      </span>
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a
                      className="!text-foreground no-underline hover:no-underline"
                      href={`tel:${driverBPhone}`}
                      target="_parent"
                    >
                      <Phone className="h-4 w-4 shrink-0" />
                      <span className="font-medium">{driverBName}</span>
                      <span className="ml-auto pl-4 text-muted-foreground">
                        {driverBPhone}
                      </span>
                    </a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : driverPhone ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" asChild>
                      <a
                        className="!text-foreground"
                        href={`tel:${driverPhone}`}
                        target="_parent"
                        aria-label={
                          minimal ? t("chatHeader.callDriver") : undefined
                        }
                      >
                        <Phone className="h-4 w-4" />
                      </a>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p>{driverPhone}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
            {!hideActions && !minimal && (
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        data-testid="chat-search-button"
                        variant="ghost"
                        size="icon"
                        onClick={handleShowSearch}
                      >
                        <Search className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>{t("chatHeader.searchInMessages")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {useNewCmrSearch ? (
                  <CmrSearchDialog
                    open={cmrSearchOpen}
                    onClose={() => setCmrSearchOpen(false)}
                    onNavigateToChat={(jid) => {
                      dispatch(changeActiveChat({ activeChatJid: jid }))
                      dispatch(changeActiveTab({ activeTab: TAB_TYPES.CHATS }))
                      setCmrSearchOpen(false)
                    }}
                  />
                ) : (
                  <SearchPopover
                    isOpen={searchVisible}
                    onClose={handleCloseSearch}
                    chatJid={chat.jid}
                    key={chat.jid}
                  />
                )}
              </>
            )}
            <ChatHeaderRoomActions
              variant={minimal ? "agent" : "classic"}
              hidden={Boolean(hideActions)}
              actions={{
                edit: canEdit && handleEdit ? handleEdit : undefined,
                info: canOpenChatInfo
                  ? {
                      kind: routeJid ? "car" : "chat",
                      label: routeJid
                        ? t("chatHeader.carInfo")
                        : t("chatHeader.chatInfo"),
                      onOpen: onOpenChatInfo,
                    }
                  : undefined,
                issueLines: issues?.hasAnyIssue
                  ? buildIssueLines(issues, t)
                  : [],
                muted: isMuted,
                onToggleMuted: toggleChatMuted,
                archived: isArchived,
                onToggleArchived: toggleChatArchived,
                leave: canLeaveChat ? onExitChat : undefined,
              }}
            />
          </div>
        </div>
        {minimal && (
          <div
            className="pointer-events-none absolute inset-x-0 top-full h-6 bg-gradient-to-b from-sidebar to-sidebar/0"
            aria-hidden
          />
        )}
        {(isMobile || isSmallScreen) && driversToSwitch.length > 0 && (
          <div className="flex min-w-0 justify-center gap-2 bg-background py-2">
            {driversToSwitch.map((driver) => (
              <DriverButton
                key={driver.driverJid}
                {...driver}
                onActiveChatChange={onActiveChatChange}
              />
            ))}
          </div>
        )}
      </div>
      {(isMobile || isSmallScreen) && tachos.length > 0 && tachoEnabled && (
        <div className="border-b border-border bg-background px-4 py-1">
          {tachos.map(({ tacho }) => (
            <TimeProgressBar
              key={tacho.jid}
              usedSecondsToday={tacho.usedSecondsToday ?? 0}
              remainingSecondsToday={tacho.remainingSecondsToday ?? 0}
              remainingSecondsThisWeek={tacho.remainingSecondsThisWeek}
              remainingWeeklyRestSeconds={tacho.remainingWeeklyRestSeconds}
              remainingSecondsTwoWeeks={tacho.remainingSecondsTwoWeeks}
              remainingSecondsOfUninterruptedDriving={
                tacho.remainingSecondsOfUninterruptedDriving
              }
              dayEndTimestamp={tacho.dayEndTimestamp}
              remainingDailyExtensions={tacho.remainingDailyExtensions}
              isDoubleCrew={isDoubleCrewTacho}
              driverName={tacho.driverName}
              legalCounters={tacho.legalCounters}
              crew={tacho.crew}
              timestamp={tacho.timestamp}
            />
          ))}
        </div>
      )}
    </>
  )
}

export default ChatHeader
